// 通用请求缓存工具
// 作用：
// 1. 对「全站共享、低频变更」的接口结果做内存缓存，避免重复请求（如首页数据、解决方案树）。
// 2. 并发去重：同一个 key 在请求未返回前被多次调用，只发一次真实请求，其余复用同一个 Promise。
// 3. 失败不缓存，下次调用自动重试。

interface CacheEntry<T> {
  value?: T;
  expiry: number;
  promise?: Promise<T>;
}

const store = new Map<string, CacheEntry<unknown>>();

// 默认缓存 5 分钟
const DEFAULT_TTL = 5 * 60 * 1000;

/**
 * 带缓存与并发去重的请求封装。
 * @param key     缓存键（带参数的接口需把参数序列化进 key）
 * @param fetcher 实际发起请求的函数
 * @param ttl     缓存有效期（毫秒），默认 5 分钟
 */
export function cachedRequest<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number = DEFAULT_TTL,
): Promise<T> {
  const now = Date.now();
  const entry = store.get(key) as CacheEntry<T> | undefined;

  // 命中有效缓存
  if (entry && entry.value !== undefined && entry.expiry > now) {
    return Promise.resolve(entry.value);
  }

  // 命中进行中的请求 → 复用，避免并发重复请求
  if (entry && entry.promise) {
    return entry.promise;
  }

  const promise = fetcher()
    .then((value) => {
      store.set(key, { value, expiry: Date.now() + ttl });
      return value;
    })
    .catch((err) => {
      // 请求失败时清除占位，保证后续可重试
      store.delete(key);
      throw err;
    });

  store.set(key, { expiry: now + ttl, promise });
  return promise;
}

/** 清除缓存：传 key 清单条，不传清全部 */
export function clearApiCache(key?: string): void {
  if (key) store.delete(key);
  else store.clear();
}

/**
 * 预热缓存：把已经拿到的数据直接写入缓存，供后续 cachedRequest(key) 命中。
 * 用于聚合接口一次取回多份数据后，分发到各自的缓存键，避免子组件重复请求。
 */
export function primeCache<T>(key: string, value: T, ttl: number = DEFAULT_TTL): void {
  if (value === undefined || value === null) return;
  store.set(key, { value, expiry: Date.now() + ttl });
}

/**
 * 注册一个「进行中」的派生 Promise 到指定 key。
 * 用于聚合接口：在其请求尚未返回时，就让各子 key 指向「从聚合结果中提取对应字段」的 Promise，
 * 这样子组件随后调用 cachedRequest(子key) 会复用该 Promise，而不会另发请求。
 */
export function primeInFlight<T>(
  key: string,
  promise: Promise<T>,
  ttl: number = DEFAULT_TTL,
): Promise<T> {
  const wrapped = promise
    .then((value) => {
      store.set(key, { value, expiry: Date.now() + ttl });
      return value;
    })
    .catch((err) => {
      store.delete(key);
      throw err;
    });
  store.set(key, { expiry: Date.now() + ttl, promise: wrapped });
  return wrapped;
}

/** 将参数对象序列化为稳定的缓存键后缀 */
export function paramsKey(params?: Record<string, unknown>): string {
  if (!params) return '';
  const keys = Object.keys(params).sort();
  return keys.map((k) => `${k}=${String(params[k])}`).join('&');
}
